import { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Link, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { Periodo } from '@praticheenergia/domain'
import { mesiDelPeriodo } from '../letture/letture.api'
import { elencaVendite, provinceDelPeriodo, salvaVendite } from './vendite.api'
import { interpretaVendite, type RigaVendita } from './interpreta'
import { leggiFoglio } from './xlsx'

interface Props {
  readonly dichiarazioneId: number
  readonly anno: number
  readonly periodo: Periodo
  /** Falso da da_firmare in poi: l'XML e' generato, le vendite non si toccano. */
  readonly modificabile: boolean
}

const COLONNE = ['mese', 'provincia', 'tipologia', 'utenze', 'kWh'] as const
type Colonna = (typeof COLONNE)[number]
/** La griglia tiene testo: la validazione e' la stessa dell'import. */
type RigaGriglia = Record<Colonna, string>

const INTESTAZIONE = ['Mese', 'Provincia', 'Tipologia', 'Numero utenze', 'kWh']
const NESSUNA_VENDITA: RigaVendita[] = []

const inGriglia = (r: RigaVendita): RigaGriglia => ({
  mese: String(r.mese),
  provincia: r.provincia,
  tipologia: r.tipologia,
  utenze: String(r.numeroUtenze),
  kWh: r.kwh,
})

export function SchedaVendite({ dichiarazioneId, anno, periodo, modificabile }: Props) {
  const queryClient = useQueryClient()
  const [righe, setRighe] = useState<RigaGriglia[]>([])
  const [errori, setErrori] = useState<string[]>([])
  const [messaggio, setMessaggio] = useState<string | null>(null)

  const { data: salvate = NESSUNA_VENDITA } = useQuery({
    queryKey: ['vendite', dichiarazioneId],
    queryFn: () => elencaVendite(dichiarazioneId),
  })
  useEffect(() => setRighe(salvate.map(inGriglia)), [salvate])

  // Attese al momento di validare, non lette dallo stato: un import fatto
  // appena aperta la scheda altrimenti rifiuta ogni sigla (trovato dall'e2e).
  const opzioni = async () => ({
    mesi: mesiDelPeriodo(periodo),
    province: await queryClient.fetchQuery({
      queryKey: ['province', anno, periodo],
      queryFn: () => provinceDelPeriodo(anno, periodo),
    }),
  })

  async function suImporta(file: File) {
    setErrori([])
    setMessaggio(null)
    try {
      const esito = interpretaVendite(await leggiFoglio(await file.arrayBuffer()), await opzioni())
      if (esito.errori.length > 0) {
        setErrori(esito.errori)
        return
      }
      setRighe(esito.righe.map(inGriglia))
      setMessaggio(`${esito.righe.length} righe importate: controlla e salva`)
    } catch (e) {
      setErrori([e instanceof Error ? e.message : 'file non leggibile'])
    }
  }

  async function suSalva() {
    setErrori([])
    setMessaggio(null)
    const esito = interpretaVendite([INTESTAZIONE, ...righe.map((r) => COLONNE.map((c) => r[c]))], {
      ...(await opzioni()),
      primaRiga: 1,
    })
    if (esito.errori.length > 0) {
      setErrori(esito.errori)
      return
    }
    try {
      await salvaVendite(dichiarazioneId, esito.righe)
      // Anche le dichiarazioni: il loro stato lo ricalcola il trigger (0034).
      await queryClient.invalidateQueries()
      setMessaggio('Vendite salvate')
    } catch (e) {
      setErrori([e instanceof Error ? e.message : 'errore imprevisto'])
    }
  }

  const modifica = (i: number, c: Colonna, v: string) =>
    setRighe((rr) => rr.map((r, j) => (j === i ? { ...r, [c]: v } : r)))

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Link href="/modello-vendite.xlsx" download>
          Scarica il modello Excel
        </Link>
        {modificabile && (
          <Button component="label" variant="outlined">
            Importa il modello compilato
            <input
              hidden
              type="file"
              accept=".xlsx"
              aria-label="Importa il modello compilato"
              onChange={(e) => {
                const f = e.target.files?.[0]
                e.target.value = ''
                if (f) void suImporta(f)
              }}
            />
          </Button>
        )}
      </Stack>
      {!modificabile && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          La dichiarazione e gia stata generata: le vendite non si possono piu modificare.
        </Typography>
      )}
      {errori.length > 0 && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          {errori.map((e) => (
            <div key={e}>{e}</div>
          ))}
        </Alert>
      )}
      {messaggio !== null && <Alert severity="info" sx={{ mb: 2 }}>{messaggio}</Alert>}
      <Table size="small">
        <TableHead>
          <TableRow>
            {INTESTAZIONE.map((c) => (
              <TableCell key={c}>{c}</TableCell>
            ))}
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {righe.map((r, i) => (
            <TableRow key={i}>
              {COLONNE.map((c) => (
                <TableCell key={c}>
                  <TextField
                    size="small"
                    label={`Riga ${i + 1}, ${c}`}
                    value={r[c]}
                    disabled={!modificabile}
                    onChange={(e) => modifica(i, c, e.target.value)}
                  />
                </TableCell>
              ))}
              <TableCell>
                {modificabile && (
                  <Button
                    size="small"
                    color="error"
                    aria-label={`Rimuovi riga ${i + 1}`}
                    onClick={() => setRighe((rr) => rr.filter((_, j) => j !== i))}
                  >
                    Rimuovi
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        {modificabile && (
          <Button
            onClick={() => setRighe((rr) => [...rr, { mese: '', provincia: '', tipologia: '', utenze: '', kWh: '' }])}
          >
            Aggiungi riga
          </Button>
        )}
        <Button variant="contained" disabled={!modificabile} onClick={() => void suSalva()}>
          Salva vendite
        </Button>
      </Stack>
    </Box>
  )
}
